import { useEffect, useState } from "react"
import { pmndpAPI } from "../../../services/pmndp.api";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import PMNDPModal from './PMNDPModal';
import { PMNDPFormField, PMNDPFormSection, PMNDPFormButtons } from './PMNDPForm';

const SlotManagementModal = ({ onClose, getSlots, editData }) => {
    const labels = {
        edit: "Edit",
        addEdit: "Add/Edit",
        machineSlots: "Machine Slots",
        close: "Close",
        machineDetails: "Machine Details:",
        date: "Date",
        machineSerialNo: "Machine sr. No",
        machineSerialNoType: "Machine sr. No Type",
        addSlot: "Add Slot:",
        more: "More",
        startTime: "Start Time:",
        endTime: "End Time:",
        save: "Save",
        cancel: "Cancel",
        dateRequired: "Date is required",
        machineRequired: "Machine is required",
        startTimeRequired: "Start time is required",
        endTimeRequired: "End time is required",
        endTimeAfterStart: "End time must be after start time",
        slotUpdated: "Slot updated successfully!",
        slotCreated: "Slot created successfully!",
        failedToSaveSlot: "Failed to save slot",
        errorSavingSlot: "Error occurred while saving slot",
        failedToLoadMachines: "Failed to load machines"
    };

    const [slotDate, setSlotDate] = useState('');
    const [machineId, setMachineId] = useState('');
    const [facilityId, setFacilityId] = useState(null); // Default fallback
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [machines, setMachines] = useState([]);
    const [formErrors, setFormErrors] = useState({});

    // For slots, handle both add and edit
    const getCurrentTime = () => {
        const now = new Date();
        return now.toTimeString().slice(0, 5);
    };

    const [slots, setSlots] = useState(() => [
        { startTime: getCurrentTime(), endTime: '' }
    ]);

    // Prefill form for edit
    useEffect(() => {
        if (editData) {
            setSlotDate(editData.slot_date || '');
            setMachineId(editData.machine_id ? String(editData.machine_id) : '');
            if (Array.isArray(editData.slots) && editData.slots.length > 0) {
                setSlots(editData.slots.map(slot => ({
                    startTime: slot.start_time,
                    endTime: slot.end_time,
                    slot_id: slot.slot_id,
                    is_public: slot.is_public,
                    isDirty: slot.isDirty,
                    assignedTo: slot.assigned_to,
                    isActive: slot.isActive,
                    isDelete: slot.isDelete,
                    inexData: slot.inexData
                })));
            }
        }
    }, [editData]);

    const handleAddSlot = () => {
        const newSlot = {
            startTime: getCurrentTime(),
            endTime: '',
            slot_id: null, // New slots don't have slot_id yet
            is_public: 0,
            isDirty: 0,
            assignedTo: null,
            isActive: true,
            isDelete: false,
            inexData: 0
        };
        setSlots([...slots, newSlot]);
    };

    const handleChange = (index, field, value) => {
        const updatedSlots = [...slots];
        updatedSlots[index][field] = value;

        // Reset endTime if new startTime is after current endTime
        if (field === 'startTime' && updatedSlots[index].endTime && value > updatedSlots[index].endTime) {
            updatedSlots[index].endTime = '';
        }

        setSlots(updatedSlots);
    };

    const handleDelete = (index) => {
        const updatedSlots = slots.filter((_, i) => i !== index);
        setSlots(updatedSlots);
    };

    // Fetch machine list dynamically
    useEffect(() => {
        const fetchMachines = async () => {
            setIsPageLoading(true);
            try {
                const resp = await pmndpAPI.getMachineList();
                const machineArr = resp?.data?.dataGet || [];
                setMachines(Array.isArray(machineArr) ? machineArr : []);
            } catch (error) {
                console.error('Error fetching machines:', error);
                toast.error(labels.failedToLoadMachines);
                setMachines([]);
            }
            setIsPageLoading(false);
        };
        fetchMachines();

        // Parse and set facilityTypeId from loggedinUserdetails in localStorage
        const initializeFacilityId = () => {
            try {
                const userDetailsStr = localStorage.getItem('loggedinUserdetails');
                if (!userDetailsStr) {
                    console.warn('No user details found in localStorage');
                    return;
                }
                
                const userDetails = JSON.parse(userDetailsStr);
                if (userDetails?.facilityTypeId) {
                    setFacilityId(userDetails.facilityTypeId);
                } else {
                    console.warn('No facilityTypeId found in user details');
                }
            } catch (error) {
                console.error('Failed to parse user details from localStorage:', error);
                toast.error('Failed to load user settings');
            }
        };
        
        initializeFacilityId();
    }, []);

    // Handle form submit (create or update)
    const handleSubmit = async (e) => {
        e.preventDefault();

        // --- Form validation ---
        const errors = {};
        if (!slotDate) errors.slotDate = labels.dateRequired;
        if (!machineId) errors.machineId = labels.machineRequired;
        slots.forEach((slot, idx) => {
            if (!slot.startTime) errors[`startTime${idx}`] = labels.startTimeRequired;
            if (!slot.endTime) errors[`endTime${idx}`] = labels.endTimeRequired;
            if (slot.startTime && slot.endTime && slot.endTime <= slot.startTime) {
                errors[`endTime${idx}`] = labels.endTimeAfterStart;
            }
        });

        setFormErrors(errors);
        if (Object.keys(errors).length > 0) return;

        setIsPageLoading(true);

        const formattedSlots = formatSlotsForSubmission(slots, editData);

        // Prepare payload for update or create
        const payload = {
            id: editData?.id || null,
            slot_date: slotDate,
            machine_id: machineId ? parseInt(machineId) : null,
            slots: formattedSlots,
            facility_id: facilityId
        };

        try {
            const resp = await pmndpAPI.createSlot(payload);

            if (resp && resp.success) {
                toast.success(editData ? labels.slotUpdated : labels.slotCreated);

                if (typeof getSlots === 'function') {
                    try {
                        await getSlots();
                    } catch (refreshError) {
                        console.error('Error refreshing slots:', refreshError);
                        toast.warn('Slot saved but failed to refresh list');
                    }
                }

                setTimeout(() => {
                    try {
                        if (typeof onClose === 'function') onClose();
                    } catch (callbackError) {
                        console.error('Error closing modal:', callbackError);
                    }
                }, 1200);
            } else {
                const errorMsg = resp?.msg || resp?.message || labels.failedToSaveSlot;
                toast.error(errorMsg);
                console.error('API Error:', resp);
            }
        } catch (error) {
            console.error('Error saving slot:', error);
            const errorMsg = error?.response?.data?.message || error?.message || labels.errorSavingSlot;
            toast.error(errorMsg);
        } finally {
            setIsPageLoading(false);
        }
    };

    const formatSlotsForSubmission = (slots, isEdit) => {
        if (isEdit) {
            return slots.map((slot) => {
                const formattedSlot = {
                    start_time: slot.startTime,
                    end_time: slot.endTime,
                    is_public: slot.is_public ?? 0,
                    isDirty: slot.isDirty ?? 0,
                    assignedTo: slot.assignedTo ?? ""
                };
                
                // Only include these fields for existing slots (with slot_id)
                if (slot.slot_id) {
                    formattedSlot.slot_id = slot.slot_id;
                    formattedSlot.isActive = slot.isActive ?? true;
                    formattedSlot.isDelete = slot.isDelete ?? false;
                    formattedSlot.inexData = slot.inexData ?? 0;
                }
                
                return formattedSlot;
            });
        }
        
        return slots.map((slot) => ({
            start_time: slot.startTime,
            end_time: slot.endTime,
            is_public: "",
            isDirty: 0,
            assignedTo: ""
        }));
    };

    const machineOptions = machines.map(machine => ({
        value: machine.id,
        label: machine.sr_no
    }));

    // Get date range for new slots (8 days from today)
    const getDateRange = () => {
        const today = new Date();
        const minDate = today.toISOString().split('T')[0];
        const maxDate = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        return { minDate, maxDate };
    };

    const { minDate, maxDate } = getDateRange();

    return (
        <>
            
            {isPageLoading && <Loader />}

            <PMNDPModal
                isOpen={true}
                onClose={onClose}
                title={`${editData ? labels.edit : labels.addEdit} ${labels.machineSlots}`}
                icon="fa-calendar-plus"
                size="xlarge"
                footer={
                    <PMNDPFormButtons
                        onSave={handleSubmit}
                        onCancel={onClose}
                        saveText={editData ? "Update Slots" : labels.save}
                        cancelText={labels.cancel}
                        isLoading={isPageLoading}
                        disabled={Object.keys(formErrors).length > 0}
                    />
                }
            >
                <form onSubmit={handleSubmit}>
                    <PMNDPFormSection title={labels.machineDetails} icon="fa-cogs">
                        <PMNDPFormField
                            label={labels.date}
                            type="date"
                            value={slotDate}
                            onChange={(e) => setSlotDate(e.target.value)}
                            error={formErrors.slotDate}
                            disabled={!!editData}
                            min={!editData ? minDate : undefined}
                            max={!editData ? maxDate : undefined}
                            required
                        />

                        <PMNDPFormField
                            label={labels.machineSerialNo}
                            type="select"
                            value={machineId}
                            onChange={(e) => setMachineId(e.target.value)}
                            error={formErrors.machineId}
                            options={machineOptions}
                            placeholder="Select machine"
                            disabled={!!editData}
                            required
                        />
                    </PMNDPFormSection>

                    <PMNDPFormSection
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginRight: '20px' }}>
                                <span>{labels.addSlot}</span>
                                <button
                                    type="button"
                                    className="pmndp-btn pmndp-btn-secondary"
                                    onClick={handleAddSlot}
                                    style={{ fontSize: '12px', padding: '8px 16px' }}
                                >
                                    <i className="fa fa-plus"></i>
                                    Add More Slot
                                </button>
                            </div>
                        }
                        icon="fa-clock-o"
                    >
                        {slots.map((slot, index) => (
                            <div key={index} style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr auto',
                                gap: '15px',
                                alignItems: 'end',
                                padding: '15px',
                                background: '#f8fafc',
                                borderRadius: '12px',
                                border: '1px solid #e2e8f0',
                                marginBottom: '15px'
                            }}>
                                <PMNDPFormField
                                    label={`${labels.startTime} ${index + 1}`}
                                    type="time"
                                    value={slot.startTime}
                                    onChange={(e) => handleChange(index, 'startTime', e.target.value)}
                                    error={formErrors[`startTime${index}`]}
                                    required
                                />

                                <PMNDPFormField
                                    label={`${labels.endTime} ${index + 1}`}
                                    type="time"
                                    value={slot.endTime}
                                    onChange={(e) => handleChange(index, 'endTime', e.target.value)}
                                    error={formErrors[`endTime${index}`]}
                                    min={slot.startTime}
                                    disabled={!slot.startTime}
                                    required
                                />

                                {slots.length > 1 && (
                                    <button
                                        type="button"
                                        className="pmndp-action-btn pmndp-action-btn-danger"
                                        onClick={() => handleDelete(index)}
                                        title="Delete Slot"
                                    >
                                        <i className="fa fa-trash"></i>
                                    </button>
                                )}
                            </div>
                        ))}
                    </PMNDPFormSection>
                </form>
            </PMNDPModal>
        </>
    )
}

export default SlotManagementModal
